import React, { useEffect, useState } from "react";
import { Save, Bell, Mail, MessageSquare, ShieldCheck, AlertTriangle, RefreshCw, Database } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

const Toggle = ({ checked, onChange, testId }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
        style={{ background: checked ? "var(--copper)" : "rgba(91,107,126,0.25)" }}
        aria-pressed={checked}
    >
        <span
            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: checked ? "translateX(22px)" : "translateX(2px)" }}
        />
    </button>
);

const SystemSettingsPage = () => {
    const { user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [resetState, setResetState] = useState({ running: false, ok: null });
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const isSuperAdmin = user?.role === "super_admin";

    useEffect(() => {
        apiGet("/admin/system-settings").then(setSettings).catch(() => setSettings({ feature_flags: {}, notification_config: {} }));
    }, []);

    const toggleFlag = (key, value) => {
        setSettings({ ...settings, feature_flags: { ...settings.feature_flags, [key]: value } });
    };
    const toggleNotif = (key, value) => {
        setSettings({ ...settings, notification_config: { ...settings.notification_config, [key]: value } });
    };

    const save = async () => {
        setSaving(true);
        try {
            const fresh = await apiPatch("/admin/system-settings", {
                feature_flags: settings.feature_flags,
                notification_config: settings.notification_config,
            });
            setSettings(fresh);
            setSavedAt(new Date());
        } catch (e) {
            /* swallow */
        } finally {
            setSaving(false);
        }
    };

    // ============ Demo reset (super_admin only) ============
    const pollResetStatus = async () => {
        try {
            const s = await apiGet("/admin/demo/reset/status");
            setResetState(s);
            if (s.running) setTimeout(pollResetStatus, 2000);
        } catch {
            setResetState({ running: false, ok: false });
        }
    };

    const triggerReset = async () => {
        setResetConfirmOpen(false);
        setResetState({ running: true, ok: null });
        try {
            await apiPost("/admin/demo/reset", {});
            setTimeout(pollResetStatus, 1500);
        } catch (e) {
            setResetState({ running: false, ok: false });
        }
    };

    if (!settings) {
        return (
            <GlassCard padding="p-6">
                <Skeleton height="320px" />
            </GlassCard>
        );
    }

    const features = pt.admin.settings.feature;
    const sendgridConfigured = !!settings.sendgrid_configured;

    return (
        <div data-testid="settings-page">
            <PageHeader title={pt.admin.settings.title} subtitle={pt.admin.settings.subtitle} testId="settings-header" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <GlassCard padding="p-6" testId="settings-features">
                <h2 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                    {pt.admin.settings.features}
                </h2>
                <p className="font-body text-sm text-[color:var(--slate-mid)] mb-5">
                    {pt.admin.settings.subtitle}
                </p>
                <div className="space-y-3">
                    {Object.entries(features).map(([k, label]) => {
                        const enabled = !!settings.feature_flags?.[k];
                        return (
                            <motion.div
                                key={k}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center justify-between p-3 rounded-lg hover:bg-[color:var(--copper-subtle)] transition-colors"
                                data-testid={`flag-${k}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className="rounded-md p-2"
                                        style={{
                                            background: enabled ? "var(--copper-subtle)" : "rgba(91,107,126,0.08)",
                                            color: enabled ? "var(--copper-dark)" : "var(--slate-light)",
                                        }}
                                    >
                                        <ShieldCheck size={15} />
                                    </div>
                                    <span className="font-body text-sm text-[color:var(--slate-deep)] font-medium">
                                        {label}
                                    </span>
                                </div>
                                <Toggle checked={enabled} onChange={(v) => toggleFlag(k, v)} testId={`toggle-${k}`} />
                            </motion.div>
                        );
                    })}
                </div>
            </GlassCard>

            <GlassCard padding="p-6" testId="settings-channels">
                <h2 className="font-display text-2xl text-[color:var(--slate-deep)] mb-1">
                    {pt.admin.settings.notifications}
                </h2>
                <p className="font-body text-sm text-[color:var(--slate-mid)] mb-5">
                    Configure os canais activos da plataforma.
                </p>
                <div className="space-y-3">
                    {[
                        { k: "email", label: pt.admin.settings.channel_email, Icon: Mail },
                        { k: "in_app", label: pt.admin.settings.channel_inapp, Icon: Bell },
                        { k: "sms", label: pt.admin.settings.channel_sms, Icon: MessageSquare },
                    ].map(({ k, label, Icon }) => {
                        const enabled = !!settings.notification_config?.[k];
                        return (
                            <div
                                key={k}
                                className="flex items-center justify-between p-3 rounded-lg hover:bg-[color:var(--copper-subtle)] transition-colors"
                                data-testid={`notif-${k}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className="rounded-md p-2"
                                        style={{
                                            background: enabled ? "var(--copper-subtle)" : "rgba(91,107,126,0.08)",
                                            color: enabled ? "var(--copper-dark)" : "var(--slate-light)",
                                        }}
                                    >
                                        <Icon size={15} />
                                    </div>
                                    <span className="font-body text-sm text-[color:var(--slate-deep)] font-medium">
                                        {label}
                                    </span>
                                </div>
                                <Toggle checked={enabled} onChange={(v) => toggleNotif(k, v)} testId={`toggle-notif-${k}`} />
                            </div>
                        );
                    })}
                </div>

                {/* SendGrid status hint */}
                <div
                    className="mt-5 p-3 rounded-lg flex items-start gap-3"
                    style={{
                        background: sendgridConfigured ? "rgba(46,125,50,0.06)" : "rgba(245,124,0,0.08)",
                        border: `1px solid ${sendgridConfigured ? "rgba(46,125,50,0.18)" : "rgba(245,124,0,0.25)"}`,
                    }}
                    data-testid="sendgrid-hint"
                >
                    {sendgridConfigured ? (
                        <ShieldCheck size={18} className="text-[color:var(--success)] mt-0.5" />
                    ) : (
                        <AlertTriangle size={18} className="text-[color:var(--warning)] mt-0.5" />
                    )}
                    <div>
                        <p className="font-body text-sm font-medium text-[color:var(--slate-deep)]">
                            {sendgridConfigured ? pt.admin.settings.sendgridConfigured : pt.admin.settings.sendgridMissing}
                        </p>
                        <p className="font-body text-xs text-[color:var(--slate-mid)] mt-1">
                            Defina <code className="font-mono">SENDGRID_API_KEY</code> em <code className="font-mono">backend/.env</code> para activar o envio real.
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-[color:var(--glass-border-subtle)]">
                    {savedAt && (
                        <span className="font-body text-xs text-[color:var(--success)]" data-testid="saved-feedback">
                            {pt.admin.settings.saved}
                        </span>
                    )}
                    <NxButton
                        variant="copper"
                        onClick={save}
                        loading={saving}
                        icon={<Save size={15} />}
                        testId="settings-save"
                    >
                        {pt.admin.settings.save}
                    </NxButton>
                </div>
            </GlassCard>
            </div>

            {/* Demo Reset — super_admin only, full-width destructive panel */}
            {isSuperAdmin && (
                <GlassCard padding="p-6" className="mt-5" testId="demo-reset-card">
                    <div className="flex items-start gap-4">
                        <div
                            className="rounded-xl p-3 flex-shrink-0"
                            style={{ background: "rgba(192,57,43,0.08)", color: "var(--error)" }}
                        >
                            <Database size={22} strokeWidth={1.4} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-display text-2xl text-[color:var(--slate-deep)]">
                                {pt.demoReset?.cardTitle || "Reset Demo Data"}
                            </h2>
                            <p className="font-body text-sm text-[color:var(--slate-mid)] mt-1">
                                {pt.demoReset?.cardDesc || "Apaga e recria a totalidade dos dados de demonstração — utilizadores demo, clientes, ordens, tickets, deals, KPIs, conectores. Operação destrutiva. Pode demorar até 90 segundos."}
                            </p>
                            {resetState.running && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                                    style={{ background: "var(--copper-subtle)", color: "var(--copper-dark)" }}
                                    data-testid="reset-running"
                                >
                                    <RefreshCw size={13} className="animate-spin" />
                                    <span className="font-body text-xs font-medium">{pt.demoReset?.running || "A repor dados de demonstração…"}</span>
                                </motion.div>
                            )}
                            {!resetState.running && resetState.ok === true && (
                                <p className="font-body text-sm text-[color:var(--success)] mt-3" data-testid="reset-success">
                                    {pt.demoReset?.success || "✓ Dados de demonstração repostos com sucesso. Recarregue a página."}
                                </p>
                            )}
                            {!resetState.running && resetState.ok === false && (
                                <p className="font-body text-sm text-[color:var(--error)] mt-3" data-testid="reset-error">
                                    {pt.demoReset?.error || "✕ Falha na reposição. Verifique os logs do backend."}
                                </p>
                            )}
                        </div>
                        <NxButton
                            variant="danger"
                            onClick={() => setResetConfirmOpen(true)}
                            disabled={resetState.running}
                            icon={<RefreshCw size={15} />}
                            testId="demo-reset-btn"
                        >
                            {pt.demoReset?.button || "Reset Demo"}
                        </NxButton>
                    </div>
                </GlassCard>
            )}

            {/* Confirmation modal */}
            {resetConfirmOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(31, 45, 64, 0.55)", backdropFilter: "blur(6px)" }}
                    onClick={() => setResetConfirmOpen(false)}
                    data-testid="reset-confirm-overlay"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-card max-w-md w-full p-7"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="font-display text-2xl text-[color:var(--slate-deep)] mb-2">
                            {pt.demoReset?.modalTitle || "Confirmar reposição"}
                        </h3>
                        <p className="font-body text-sm text-[color:var(--slate-mid)] mb-5">
                            {pt.demoReset?.modalBody || "Esta operação apaga todos os dados de demonstração e recria-os a partir dos seeds."} <strong>{pt.demoReset?.modalBodyStrong || "Não pode ser desfeita."}</strong> {pt.demoReset?.modalBodyTail || "Continuar?"}
                        </p>
                        <div className="flex justify-end gap-3">
                            <NxButton
                                variant="secondary"
                                onClick={() => setResetConfirmOpen(false)}
                                testId="reset-cancel"
                            >
                                {pt.common.cancel}
                            </NxButton>
                            <NxButton
                                variant="danger"
                                onClick={triggerReset}
                                icon={<RefreshCw size={15} />}
                                testId="reset-confirm"
                            >
                                {pt.demoReset?.confirm || "Sim, repor agora"}
                            </NxButton>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default SystemSettingsPage;
